import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  TextField,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { useQuery, useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
  GetTaxRuleDocument,
  UpdateTaxRuleDocument,
  DeleteTaxRuleDocument,
  type GetTaxRuleQuery,
  type UpdateTaxRuleMutation,
  type UpdateTaxRuleMutationVariables,
  type DeleteTaxRuleMutation,
  type DeleteTaxRuleMutationVariables,
} from '@/generated/root-types';
import { useAuth } from '@/app/auth/AuthContext';
import { isSuperAdmin } from '@/utils/roles';
import { useNotification } from '@/app/contexts/NotificationContext';
import { errorsFromPayload } from '@/utils/mutationErrors';
import {
  detailBackButtonSx,
  detailFieldGridSx,
  detailFieldSx,
  detailFullRowSx,
  detailHeaderRowSx,
  detailPageContainerSx,
  detailTitleSx,
} from '@/utils/detailPageLayout';

export default function TaxRuleDetail() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatDateTime = (iso: string) => new Date(iso).toLocaleString(locale);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const superAdmin = isSuperAdmin(user);

  const [name, setName] = useState('');
  const [taxRate, setTaxRate] = useState('');
  const [printText, setPrintText] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isDefault, setIsDefault] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data, loading, error, refetch } = useQuery<GetTaxRuleQuery>(GetTaxRuleDocument, {
    variables: { id: id! },
    skip: !id,
  });

  const rule = data?.taxRule;

  useEffect(() => {
    if (!rule) return;
    setName(rule.name);
    setTaxRate(String(rule.taxRate));
    setPrintText(rule.printText ?? '');
    setIsActive(rule.isActive);
    setIsDefault(rule.isDefault);
  }, [rule]);

  const [updateRule, { loading: saving }] = useMutation<
    UpdateTaxRuleMutation,
    UpdateTaxRuleMutationVariables
  >(UpdateTaxRuleDocument);

  const [deleteRule, { loading: deleting }] = useMutation<
    DeleteTaxRuleMutation,
    DeleteTaxRuleMutationVariables
  >(DeleteTaxRuleDocument);

  const handleSave = async () => {
    if (!id) return;
    const rate = parseFloat(taxRate.replace(',', '.'));
    if (Number.isNaN(rate)) {
      showError(t('errors.generic'));
      return;
    }
    try {
      const res = await updateRule({
        variables: {
          input: {
            id,
            name,
            taxRate: rate,
            isActive,
            isDefault,
            printText: printText || undefined,
          },
        },
      });
      const top = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
      if (top?.length) {
        showError(top.map((e) => e.message).join(' '));
        return;
      }
      const pe = errorsFromPayload(res.data?.updateTaxRule);
      if (pe) {
        showError(pe);
        return;
      }
      showSuccess(t('common.saved'));
      await refetch();
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      const res = await deleteRule({ variables: { input: { id } } });
      const top = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
      if (top?.length) {
        showError(top.map((e) => e.message).join(' '));
        return;
      }
      const pe = errorsFromPayload(res.data?.deleteTaxRule);
      if (pe || !res.data?.deleteTaxRule?.boolean) {
        showError(pe || t('errors.generic'));
        return;
      }
      showSuccess(t('common.deleted'));
      navigate('/tax-rules');
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  if (loading) {
    return (
      <Box sx={{ ...detailPageContainerSx, display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !rule) {
    return (
      <Box sx={detailPageContainerSx}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/tax-rules')}>
          {t('common.back')}
        </Button>
        <Typography color="error" sx={{ mt: 2 }}>
          {error?.message ?? t('errors.generic')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={detailPageContainerSx}>
      <Box sx={detailHeaderRowSx}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/tax-rules')}
          sx={detailBackButtonSx}
        >
          {t('common.back')}
        </Button>
        <Typography variant="h4" component="h1" sx={detailTitleSx}>
          {rule.name}
        </Typography>
      </Box>
      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, width: '100%', boxSizing: 'border-box' }}
      >
        <Box sx={detailFieldGridSx}>
          <TextField
            sx={detailFieldSx}
            label={t('common.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            disabled={!superAdmin}
          />
          <TextField
            sx={detailFieldSx}
            label={t('masterData.taxRate')}
            value={taxRate}
            onChange={(e) => setTaxRate(e.target.value)}
            fullWidth
            disabled={!superAdmin}
            helperText={t('masterData.taxRateHint')}
          />
          <TextField
            sx={detailFieldSx}
            label={t('common.created')}
            value={formatDateTime(rule.createdAt)}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={detailFieldSx}
            label={t('common.updated')}
            value={formatDateTime(rule.updatedAt)}
            fullWidth
            InputProps={{ readOnly: true }}
          />

          <TextField
            sx={{ ...detailFieldSx, ...detailFullRowSx }}
            label={t('masterData.printText')}
            value={printText}
            onChange={(e) => setPrintText(e.target.value)}
            fullWidth
            disabled={!superAdmin}
          />
          <FormControlLabel
            sx={{ ...detailFullRowSx, ml: 0 }}
            control={
              <Switch
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                disabled={!superAdmin}
              />
            }
            label={t('masterData.active')}
          />
          <FormControlLabel
            sx={{ ...detailFullRowSx, ml: 0 }}
            control={
              <Switch
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                disabled={!superAdmin}
              />
            }
            label={t('common.default')}
          />
          {superAdmin && (
            <Box sx={{ ...detailFullRowSx, display: 'flex', gap: 1, flexWrap: 'wrap', pt: 0.5 }}>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {t('common.save')}
              </Button>
              <Button color="error" onClick={() => setDeleteOpen(true)}>
                {t('common.delete')}
              </Button>
            </Box>
          )}
        </Box>
      </Paper>
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>{t('masterData.confirmDeleteTaxRule')}</DialogTitle>
        <DialogContent>
          <Typography>{t('masterData.confirmDeleteBody')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>{t('common.cancel')}</Button>
          <Button color="error" onClick={handleDelete} disabled={deleting}>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
